import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import CenteredSpinner from './CenteredSpinner';
import { toast } from 'react-toastify';
import ScrollToTop from 'react-scroll-to-top';
import { PropertyCard } from './PropertyCard';
import { ReasonModal } from './ReasonModal';

export const Favourites = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [userId] = useState(localStorage.getItem('userId'));
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [propertyID, setPropertyID] = useState('');
  const [propertyRef, setPropertyRef] = useState(null);
  const [properties, setProperties] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [reasonError, setReasonError] = useState('');
  const [commentError, setCommentError] = useState('');
  const [userID] = useState(localStorage.getItem('userId'));
  const navigate = useNavigate();


  useEffect(() => {
    document.title = 'Favourites';
    window.scrollTo(0, 0);
  }, []);

  const fetchFavourites = () => {
    const options = {
      method: 'POST',
      url: '/propertyfavourite/getfavouriteproperties',
      data: {
        userId: userId,
        localityId: 0,
        propertyTypeId: 0,
        favouriteReasonId: 0,
        allFavourites: false,
        includeImages: true,
      },
    };

    return axios
      .request(options)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw new Error('Server error');
      });
  };

  const handleDelete = async (id) => {
    const options = {
      method: 'DELETE',
      url: `/propertyfavourite/${id.id}`,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    axios
      .request(options)
      .then((response) => {
        toast.success(
          `Property: ${id.propertyRef} has been removed`,
          {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
      })
      .catch((error) => {
        throw new Error('Server error');
      });
  };

  const handleReasonChange = (event) => {
    setReasonError(null);
    setReason(event.value);
    setDescription(event.label);
  };

  const fetchReason = () => {
    const options = {
      method: 'GET',
      url: `/propertyfavouritereason`,
    };

    return axios
      .request(options)
      .then((response) => {
        let options = response.data.map((element) => {
          return {
            value: element.id,
            label: element.description,
          };
        });
        return options;
      })
      .catch((error) => {});
  };

  const {
    isLoading,
    isError,
    error,
    data: favouritesData,
  } = useQuery(['Favourites'], fetchFavourites);

  const mutation = useMutation(handleDelete, {
    onMutate: async (editedValue) => {
      await queryClient.cancelQueries('Favourites');
      const previousValue = queryClient.getQueryData(['Favourites'], {
        exact: false,
      });
      const updatedValue = [...previousValue];
      const removeDeleted = updatedValue.filter(
        (eachValue) => eachValue.favouriteObj.id !== editedValue.id,
      );
      queryClient.setQueryData('Favourites', [...removeDeleted]);

      return { previousValue };
    },
    onError: (err, _id, context) => {
      queryClient.setQueryData('Favourites', context.previousValue);
      toast.error(`Server error, Please try again later`, {
        position: 'top-left',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });

  const { data: reasonData } = useQuery(['Reason'], fetchReason);

  const reasonModal = (e, id, propertyRef, localReason = null) => {
    if (localReason) {
      setReason(localReason);
    }
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
    setPropertyID(id);
    setPropertyRef(propertyRef);
  };

  const handleComment = (e) => {
    setCommentError(null);
    setComment(e.target.value);
  };

  const addFavourite = async (e) => {
    setDisabled(true);
    if (!reason) {
      setReasonError("Reason input can't be emptied");
      setDisabled(false);
      return;
    }
    if (!comment) {
      setCommentError("Comment input can't be emptied");
      setDisabled(false);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    let d = new Date();
    let isoDate = d.toISOString();

    const config = {
      method: 'POST',
      url: '/propertyfavourite',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        propertyId: propertyID,
        sys_UserId: userID,
        dateRegistered: isoDate,
        propertyFavouriteReasonId: reason,
        comments: comment,
      }),
    };

    try {
      const data = await axios(config);
      if (data.status == 201) {
        setReason('');
        setComment('');
        setDisabled(false);
        toast.success(
          `Property: ${propertyRef} has been added to Favourites`,
          {
            position: 'top-center',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
        setShowModal(false);
      }
    } catch (err) {
      if (
        err.response.status === 400 &&
        err.response.data.message ===
          'bizrule_propertyFavourite_favouriteReasonAlreadyExists'
      )
        toast.error(
          `Property: ${propertyRef} wasn't added, It has been already added`,
          {
            position: 'top-left',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
      if (err.response.status === 500)
        toast.error(
          `Server error. Property: ${propertyRef} wasn't added, try again later`,
          {
            position: 'top-left',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
            progress: undefined,
          },
        );
      setComment('');
      setDisabled(false);
    }
  };

  const filterFavouriteData = (favouritesDatafilter, search) => {
    if (!search) {
      return favouritesDatafilter;
    }

    return favouritesDatafilter.filter((data) => {
      return (
        data?.propertyTypeName
          .replace(/,/g, '')
          .toLowerCase()
          .includes(search.replace(/,/g, '').toLowerCase()) ||
        data?.localityName
          .replace(/,/g, '')
          .toLowerCase()
          .includes(search.replace(/,/g, '').toLowerCase()) ||
        data?.propertyStateName
          .replace(/,/g, '')
          .toLowerCase()
          .includes(search.replace(/,/g, '').toLowerCase()) ||
        data?.propertyRef.includes(search)
      );
    });
  };

  const filterFavouriteDatas = filterFavouriteData(
    favouritesData,
    search,
  );

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (isError || error) {
    return (
      <div className="container">
        <div className="row justify-content-center align-items-center vh-100">
          <h1>Server is down, please try again later </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="container vh-100">
      <ScrollToTop smooth color="#6f00ff" />
      <div className='row'>
        <div className="col-9 col-xl-9 col-xl-9 ml-0 p-0">
          <div className="h4 mb-3 ml-0 font-weight-bold">
            <i className="far fa-heart" aria-hidden="true"></i> My
            Favourites
          </div>
          <div className="h6 mb-3 ml-0">
            You have{' '}
            {filterFavouriteDatas?.length > 1
              ? `${filterFavouriteDatas?.length} listed properties`
              : `${filterFavouriteDatas?.length} listed property`}{' '}
           
          </div>
        </div>
        <div className="col-3 col-md-3 col-xl-3 text-right">
          <p className="btn btn-outline-secondary d-block" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-2" style={{ color: '#6c757d' }}></i>
            Back
          </p>
        </div>
      </div>
      <div className="row">
        <div className="form-group has-search w-100">
          <span className="fa fa-search form-control-feedback"></span>
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {filterFavouriteDatas?.length < 1 ? (
          <h4 className="mx-auto">No search results found</h4>
        ) : null}
        <div className="container pl-0 pr-0">
          <div className="row mb-4">
            {filterFavouriteDatas &&
              React.Children.toArray(
                filterFavouriteDatas?.map((property, index) => (
                  <PropertyCard
                    property={property}
                    index={index}
                    location={location}
                    mutation={mutation}
                    showRemoveFavouriteBtn={true}
                    showHotBtn={true}
                    reasonModal={reasonModal}
                   />
                )),
              )}
          </div>
        </div>
      </div>
      <ReasonModal
          showModal={showModal}
          reason={reason}
          setReason={setReason}
          setReasonError={setReasonError}
          setCommentError={setCommentError}
          setShowModal={setShowModal}
          reasonError={reasonError}
          handleReasonChange={handleReasonChange}
          reasonData={reasonData}
          comment={comment}
          handleComment={handleComment}
          commentError={commentError}
          disabled={disabled}
          addFavourite={addFavourite}
        />
    </div>
  );
};
